// Copyright 2026. WebPros International GmbH. All rights reserved.

import * as React from 'react';
import { CancelTokenSource } from 'axios';
import { Loader } from 'common/components';
import { PaginatedCardList } from 'client/common/components/Card';
import SnapshotCard from 'common/components/ServerTabs/SnapshotTab/SnapshotCard';
import { ISnapshotResponse } from 'common/api/resources/Snapshot';
import { IPaginateApiResponse } from 'common/api/resources/Response';
import {
    ICONS,
    INTENT_TYPE,
    VIEW_TYPE,
} from 'common/constants';
import { StyledList } from 'client/common/styles/List';
import List from 'common/components/List/List';
import {
    Status,
    Translate,
} from '@plesk/ui-library';
import {
    SNAPSHOT_STATUS_AVAILABLE,
    SNAPSHOT_STATUS_FAILED,
    SNAPSHOT_STATUS_PROCESSING,
} from 'common/modules/project/constants';
import moment from 'moment';
import ButtonWithConfirmation from 'common/components/ButtonWithConfirmation';
import { EmptyView } from 'common/components/EmptyView/EmptyView';
import {
    IServerLimitResponse,
    IVmResponse,
} from 'common/api/resources/ComputeResourceVm';
import { bindedActionCreators } from 'common/redux/types';
import * as serverActions from 'common/modules/computeResourceVm/actions';
import * as snapshotsServerActions from 'common/modules/snapshot/actions';
import {
    getActionColumnProps,
    reloadListData,
} from 'common/helpers/list';
import { dataCySelector } from 'common/tests/selectors';
import { SNAPSHOT_ACTIONS } from 'common/components/ServerTabs/SnapshotTab/constants/test';
import LimitsGrid from 'common/components/ServerTabs/components/LimitCard/LimitsGrid';
import { LimitName } from 'common/api/resources/Plan';
import { useRequestCancellationEffect } from 'common/hooks/useRequestCancellationEffect';

export interface ISnapshotListProps {
    view: VIEW_TYPE;
    snapshotActions: bindedActionCreators<typeof snapshotsServerActions>;
    server: IVmResponse;
    isLoading: boolean;
    snapshots: IPaginateApiResponse<ISnapshotResponse[]>;
    serverId: number;
    canRevertServersSnapshot: boolean;
    canDeleteServersSnapshot: boolean;
    serverLimits: IServerLimitResponse[];
    serverActions: bindedActionCreators<typeof serverActions>;
}

const columns = [{
    width: '15%',
    key: 'colName',
    title: <Translate content="snapshots.list.colName" />,
},
{
    width: '15%',
    key: 'colStatus',
    title: <Translate content="snapshots.list.colStatus" />,
    className: 'header-status',
    cellProps: {
        className: 'cell-status',
    },
}, {
    width: '15%',
    key: 'colSize',
    title: <Translate content="snapshots.list.colSize" />,
}, {
    width: '15%',
    key: 'colCreated',
    title: <Translate content="snapshots.list.colCreated" />,
}, getActionColumnProps(),
];

export const SnapshotList: React.FC<ISnapshotListProps> = ({
    view,
    snapshots,
    snapshotActions: {
        getSnapshots,
        revertSnapshot,
        removeSnapshot,
    },
    isLoading,
    server,
    serverId,
    canRevertServersSnapshot,
    canDeleteServersSnapshot,
    serverLimits,
    serverActions: {
        getServerLimits,
    },
}) => {
    const isFirstLoading = useRequestCancellationEffect((token) => {
        getServerLimits(serverId);

        return getSnapshots(serverId, undefined, token);
    }, []);

    const loadPaginated = React.useCallback(
        (page: number, cancelToken?: CancelTokenSource) => getSnapshots(serverId, { page }, cancelToken),
        [getSnapshots, serverId]
    );

    const handleRevert = (snapshot: ISnapshotResponse) => () => revertSnapshot(snapshot);
    const reloadAfterRemove = () => reloadListData(snapshots, loadPaginated);
    const handleRemove = (snapshot: ISnapshotResponse) => async () => {
        await removeSnapshot(snapshot);
        reloadAfterRemove();
    };

    const data = snapshots.data.map((item) => {
        const actionsEl = (
            <>
                {item.status !== SNAPSHOT_STATUS_PROCESSING && (
                    <>
                        {
                            canRevertServersSnapshot
                            && item.status === SNAPSHOT_STATUS_AVAILABLE
                            && !server.is_suspended
                            && (
                                <ButtonWithConfirmation
                                    icon={ICONS.TRANSFER}
                                    translations={{
                                        text: (
                                            <Translate content="snapshots.confirmationRevert.text" />
                                        ),
                                        button: (
                                            <Translate content="snapshots.confirmationRevert.button" />
                                        ),
                                        tooltip: (
                                            <Translate content="snapshots.confirmationRevert.button" />
                                        ),
                                    }}
                                    handleConfirm={handleRevert(item)}
                                    data-cy={dataCySelector(item.id, SNAPSHOT_ACTIONS.REVERT)}
                                />
                            )
                        }
                        {canDeleteServersSnapshot && (
                            <ButtonWithConfirmation
                                icon={ICONS.RECYCLE}
                                isLoading={item.isLoading}
                                translations={{
                                    text: (
                                        <Translate content="snapshots.confirmationRemove.text" />
                                    ),
                                    button: (
                                        <Translate content="snapshots.confirmationRemove.button" />
                                    ),
                                    tooltip: (
                                        <Translate content="snapshots.confirmationRemove.button" />
                                    ),
                                }}
                                handleConfirm={handleRemove(item)}
                                data-cy={dataCySelector(item.id, SNAPSHOT_ACTIONS.DELETE)}
                            />
                        )}
                    </>
                )}
            </>
        );

        return {
            colName: <div style={{ marginLeft: '12px', fontWeight: 600 }}>{item.name}</div>,
            colStatus: (
                <Status
                    intent={item.status !== SNAPSHOT_STATUS_FAILED ? INTENT_TYPE.SUCCESS : INTENT_TYPE.DANGER}
                    progress={item.status === SNAPSHOT_STATUS_PROCESSING}
                    compact={item.status !== SNAPSHOT_STATUS_PROCESSING}
                >
                    {item.status !== SNAPSHOT_STATUS_PROCESSING && (
                        <Translate content={'snapshots.status.' + item.status} />
                    )}
                </Status>
            ),
            colSize: <>{item.size} GiB</>,
            colCreated: moment(item.created_at).format('DD/MM/YYYY HH:mm'),
            colActions: actionsEl,
            key: item.id.toString(),
        };
    });

    return (
        <Loader isLoading={isFirstLoading}>
            {!!snapshots.data.length && (
                <LimitsGrid limits={serverLimits.filter((limit: IServerLimitResponse) => [
                    LimitName.SnapshotsCount,
                    LimitName.SnapshotsSize,
                ].includes(limit.name))} />
            )}

            {view === VIEW_TYPE.CARD && (
                snapshots.data.length
                    ? (
                        <PaginatedCardList
                            items={snapshots.data}
                            meta={snapshots.meta}
                            isLoading={isLoading}
                            isFirstLoading={isFirstLoading}
                            loadPaginated={loadPaginated}
                            component={snapshot => (
                                <SnapshotCard
                                    key={snapshot.id}
                                    snapshot={snapshot}
                                    onRemoved={reloadAfterRemove}
                                />
                            )}
                        />
                    )
                    : (
                        <EmptyView
                            title="snapshots.emptyView.title"
                            description="snapshots.emptyView.description"
                            icon={ICONS.CAMERA}
                        />
                    )
            )}

            {view === VIEW_TYPE.LIST && (
                <StyledList>
                    <List
                        columns={columns}
                        data={data}
                        meta={snapshots.meta}
                        loadItems={loadPaginated}
                        isLoading={isLoading}
                        isFirstLoading={isFirstLoading}
                        emptyView={
                            <EmptyView
                                title="snapshots.emptyView.title"
                                description="snapshots.emptyView.description"
                                icon={ICONS.CAMERA}
                            />
                        }
                    />
                </StyledList>
            )}
        </Loader>
    );
};
